import { Sprite, type Texture } from "pixi.js";
import { SpriteAnimator } from "./SpriteAnimator";

/** Cardinal/intercardinal direction labels. */
export type Dir4 = "down" | "left" | "right" | "up";
export type Dir8 = Dir4 | "down-left" | "down-right" | "up-left" | "up-right";

export type Heading = number; // radians, 0 = right, π/2 = down

const TWO_PI = Math.PI * 2;
const PI_OVER_4 = Math.PI / 4;
const PI_OVER_8 = Math.PI / 8;

export interface DirectionalAnimatorOptions {
  /** "4" = N/E/S/W only (default), "8" = adds diagonals. */
  resolution?: "4" | "8";
  /** Frame rate. */
  fps?: number;
  /** Loop animations by default. */
  loop?: boolean;
}

/**
 * Switches between per-direction sprite animations based on a heading angle.
 *
 *   const da = new DirectionalAnimator(framesByDir, { fps: 8, resolution: "4" });
 *   da.attach(sprite);
 *   game.addUpdateSystem({ update: (dt) => {
 *     da.setHeading(entity.heading);
 *     da.update(dt, sprite);
 *   }});
 *
 * Direction is derived from heading or, if `forceDir` is set, overridden. The
 * underlying SpriteAnimator handles frame stepping for the selected clip.
 */
export class DirectionalAnimator {
  private framesByDir: Partial<Record<Dir8, Texture[]>>;
  private resolution: "4" | "8";
  private animator: SpriteAnimator;
  private currentDir: Dir8;
  private forced: Dir8 | undefined;
  private fps: number;
  private loop: boolean;

  constructor(framesByDir: Partial<Record<Dir8, Texture[]>>, opts: DirectionalAnimatorOptions = {}) {
    this.framesByDir = framesByDir;
    this.resolution = opts.resolution ?? "4";
    this.fps = opts.fps ?? 8;
    this.loop = opts.loop ?? true;
    this.currentDir = "down";
    const initial = this.framesFor(this.currentDir);
    this.animator = new SpriteAnimator(initial, { fps: this.fps, loop: this.loop });
  }

  /** Replace the per-direction frame map (e.g. on state change). */
  setFrames(framesByDir: Partial<Record<Dir8, Texture[]>>, opts: DirectionalAnimatorOptions = {}): void {
    this.framesByDir = framesByDir;
    if (opts.fps !== undefined) this.fps = opts.fps;
    if (opts.loop !== undefined) this.loop = opts.loop;
    if (opts.resolution !== undefined) this.resolution = opts.resolution;
    this.animator.setFrames(this.framesFor(this.currentDir), { fps: this.fps, loop: this.loop });
  }

  attach(sprite: Sprite): void {
    this.animator.attach(sprite);
  }

  setHeading(heading: Heading): void {
    if (this.forced) return;
    const dir = headingToDir(heading, this.resolution);
    if (dir !== this.currentDir) this.changeDir(dir);
  }

  /** Override automatic direction with a fixed one until cleared. */
  forceDirection(dir: Dir8 | undefined): void {
    this.forced = dir;
    if (dir) this.changeDir(dir);
  }

  pause(): void {
    this.animator.paused = true;
  }
  resume(): void {
    this.animator.paused = false;
  }
  reset(): void {
    this.animator.reset();
  }

  update(dt: number, sprite?: Sprite): void {
    this.animator.update(dt, sprite);
  }

  get direction(): Dir8 {
    return this.currentDir;
  }

  private changeDir(dir: Dir8): void {
    this.currentDir = dir;
    this.animator.setFrames(this.framesFor(dir), { fps: this.fps, loop: this.loop });
  }

  private framesFor(dir: Dir8): Texture[] {
    const f = this.framesByDir[dir];
    if (f && f.length > 0) return f;
    // Fallback for missing diagonals → use the closer cardinal.
    if (dir.includes("-")) {
      const cardinals = dir.split("-") as Dir4[];
      for (const c of cardinals) {
        const ff = this.framesByDir[c];
        if (ff && ff.length > 0) return ff;
      }
    }
    // Last fallback: any direction available.
    for (const k of Object.keys(this.framesByDir) as Dir8[]) {
      const ff = this.framesByDir[k];
      if (ff && ff.length > 0) return ff;
    }
    throw new Error("DirectionalAnimator received empty framesByDir");
  }
}

/** Map heading (radians) → cardinal/intercardinal label. */
export function headingToDir(heading: Heading, resolution: "4" | "8" = "4"): Dir8 {
  let h = heading % TWO_PI;
  if (h < 0) h += TWO_PI;
  if (resolution === "4") {
    // 4 wedges centered on cardinals: right, down, left, up
    if (h < PI_OVER_4 || h >= TWO_PI - PI_OVER_4) return "right";
    if (h < Math.PI - PI_OVER_4) return "down";
    if (h < Math.PI + PI_OVER_4) return "left";
    return "up";
  }
  // 8 wedges (each 45°), centered on cardinals/diagonals
  const wedge = Math.floor((h + PI_OVER_8) / PI_OVER_4) % 8;
  switch (wedge) {
    case 0:
      return "right";
    case 1:
      return "down-right";
    case 2:
      return "down";
    case 3:
      return "down-left";
    case 4:
      return "left";
    case 5:
      return "up-left";
    case 6:
      return "up";
    default:
      return "up-right";
  }
}
